/**
 * Helpers for code that reads or writes the address bar directly instead of going through React
 * Router. Under <Router basename="/sg">, useLocation() is region-relative ("/dashboard") but
 * window.location.pathname is not ("/sg/dashboard"), and window.location / history / plain hrefs
 * do not get the basename added. See region.js for the URL rules.
 */
import { DEFAULT_REGION, REGIONS, SITE_ORIGIN, canonicalUrl, getRegion, getRegionFromPath, regionHref } from "./region";

/**
 * Region-relative path: "/sg/dashboard" -> "/dashboard", "/sg" -> "/", "/about" -> "/about".
 * A query string or hash is kept: "/sg/login?next=1#x" -> "/login?next=1#x". Defaults to the address bar's pathname.
 */
export function regionRelativePath(pathname) {
  const raw =
    pathname !== undefined
      ? String(pathname || "/")
      : typeof window !== "undefined"
        ? window.location.pathname
        : "/";
  const cut = raw.search(/[?#]/);
  const path = cut === -1 ? raw : raw.slice(0, cut);
  const suffix = cut === -1 ? "" : raw.slice(cut);
  const region = getRegionFromPath(path);
  if (!region) return raw || "/";
  const rest = path.slice(REGIONS[region].base.length);
  return `${rest && rest !== "/" ? rest : "/"}${suffix}`;
}

/**
 * Full same-origin path inside the region being shown (Singapore when there is none), for
 * window.location, history.pushState/replaceState, plain <a href> and Clerk redirect props:
 * currentRegionHref("/dashboard?x=1") -> "/sg/dashboard?x=1"; currentRegionHref("/#pricing") -> "/sg#pricing".
 * Paths that already carry a region prefix are returned unchanged.
 */
export function currentRegionHref(path = "/", region = getRegion() || DEFAULT_REGION) {
  const raw = String(path ?? "/");
  if (/^[a-z][a-z0-9+.-]*:/i.test(raw) || raw.startsWith("//")) return raw;
  const withSlash = raw.startsWith("/") ? raw : `/${raw}`;
  if (getRegionFromPath(withSlash.split(/[?#]/)[0])) return withSlash;
  // "/#pricing" and "/?x=1" address the region home: "/sg#pricing", not "/sg/#pricing".
  return regionHref(region, withSlash).replace(/^(\/[a-z]+)\/([?#])/i, "$1$2");
}

/**
 * Canonical / og:url for the page. Every page lives under a region prefix (/sg, /us), so:
 * - no `url`: the address bar, with its region prefix;
 * - a region-relative path ("/login"): resolved inside the current region -> https://www.optra-labs.com/sg/login;
 * - an absolute URL that already has a region prefix, or any URL on another host: used unchanged;
 * - an absolute optra-labs.com URL without a region prefix (pre-region call sites), while a region
 *   is being shown: given the current region's prefix. On the region chooser ("/") it is left alone.
 */
export function resolveSeoUrl(url, { region = getRegion(), origin = SITE_ORIGIN, pathname = "/" } = {}) {
  if (!url) {
    return region ? `${origin}${regionHref(region, regionRelativePath(pathname))}` : `${origin}${pathname}`;
  }
  const raw = String(url).trim();
  if (raw.startsWith("/") && !raw.startsWith("//")) {
    return getRegionFromPath(raw.split(/[?#]/)[0]) ? `${SITE_ORIGIN}${raw}` : canonicalUrl(raw, region || undefined);
  }
  if (!region) return raw;
  try {
    const parsed = new URL(raw);
    const isSiteHost = /^(www\.)?optra-labs\.com$/i.test(parsed.hostname);
    if (!isSiteHost || getRegionFromPath(parsed.pathname)) return raw;
    return `${parsed.origin}${regionHref(region, parsed.pathname)}${parsed.search}${parsed.hash}`;
  } catch {
    return raw;
  }
}
